import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { formatTimeAgo, getInitials, cn } from '../lib/utils';
import axios from 'axios';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Send, Loader2, MoreVertical, Phone, Video, Image, X, Trash2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import EmojiPickerButton from '../components/chat/EmojiPicker';
import VoiceRecorder, { VoiceMessagePlayer } from '../components/chat/VoiceRecorder';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatRoomPage() {
  const { chatId } = useParams();
  const { user, token, language } = useAuth();
  const tr = t(language);
  const navigate = useNavigate();
  
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchChat = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/chats/${chatId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChat(response.data);
    } catch (error) {
      console.error('Failed to fetch chat:', error);
      navigate('/chat');
    }
  }, [chatId, token, navigate]);

  const fetchMessages = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/chats/${chatId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  }, [chatId, token]);

  useEffect(() => {
    fetchChat();
    fetchMessages();
    const interval = setInterval(fetchMessages, 3000);
    return () => clearInterval(interval);
  }, [fetchChat, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const getChatName = () => {
    if (!chat) return '';
    if (chat.is_group) return chat.group_name;
    const other = chat.participants.find(p => p.id !== user?.id);
    return other?.display_name || other?.username || 'Unknown';
  };

  const getChatAvatar = () => {
    if (!chat) return '';
    if (chat.is_group) return chat.group_avatar;
    const other = chat.participants.find(p => p.id !== user?.id);
    return other?.avatar || '';
  };

  const getOtherUserId = () => {
    if (!chat || chat.is_group) return null;
    const other = chat.participants.find(p => p.id !== user?.id);
    return other?.id;
  };

  const initiateCall = async (callType) => {
    const otherUserId = getOtherUserId();
    if (!otherUserId) {
      toast.error('Cannot call in a group chat');
      return;
    }

    try {
      const response = await axios.post(`${API}/calls/initiate`, {
        callee_id: otherUserId,
        call_type: callType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      navigate(`/call/${response.data.id}`, { state: { call: response.data } });
    } catch (error) {
      toast.error('Error calling');
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const response = await axios.post(`${API}/chats/${chatId}/messages`, {
        content: newMessage.trim(),
        message_type: 'text'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      inputRef.current?.focus();
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleSendVoice = async (audioData, duration) => {
    try {
      const response = await axios.post(`${API}/chats/${chatId}/messages`, {
        content: audioData,
        message_type: 'voice',
        metadata: { duration }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessages(prev => [...prev, response.data]);
    } catch (error) {
      console.error('Failed to send voice message:', error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum 10MB');
      return;
    }

    // Check file type
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    
    if (!isImage && !isVideo) {
      toast.error('Only images and videos accepted');
      return;
    }

    setSelectedFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (event) => {
      setMediaPreview({
        url: event.target.result,
        type: isImage ? 'image' : 'video'
      });
    };
    reader.readAsDataURL(file);
  };

  const handleSendMedia = async () => {
    if (!selectedFile || sending) return;

    setSending(true);
    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64Data = event.target.result;
        const mediaType = selectedFile.type.startsWith('image/') ? 'image' : 'video';
        
        const response = await axios.post(`${API}/chats/${chatId}/messages`, {
          content: base64Data,
          message_type: mediaType
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setMessages(prev => [...prev, response.data]);
        setSelectedFile(null);
        setMediaPreview(null);
        setSending(false);
      };
      reader.readAsDataURL(selectedFile);
    } catch (error) {
      console.error('Failed to send media:', error);
      toast.error('Error sending media');
      setSending(false);
    }
  };

  const cancelMediaPreview = () => {
    setSelectedFile(null);
    setMediaPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await axios.delete(`${API}/chats/${chatId}/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(prev => prev.filter(m => m.id !== messageId));
      toast.success('Message deleted');
    } catch (error) {
      console.error('Failed to delete message:', error);
      toast.error('Error deleting message');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen max-w-2xl mx-auto" data-testid="chat-room-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate('/chat')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          
          <Avatar className="w-10 h-10">
            <AvatarImage src={getChatAvatar()} alt={getChatName()} />
            <AvatarFallback className="bg-primary/10 text-primary">
              {getInitials(getChatName())}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1 min-w-0">
            <h1 className="font-semibold truncate">{getChatName()}</h1>
          </div>
          
          {/* Call Buttons - only for 1-1 chats */}
          {!chat?.is_group && (
            <>
              <Button 
                variant="ghost" 
                size="icon" 
                className="rounded-full"
                onClick={() => initiateCall('voice')}
                data-testid="voice-call-btn"
              >
                <Phone className="w-5 h-5" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="rounded-full"
                onClick={() => initiateCall('video')}
                data-testid="video-call-btn"
              >
                <Video className="w-5 h-5" />
              </Button>
            </>
          )}
          
          <Button variant="ghost" size="icon" className="rounded-full">
            <MoreVertical className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-muted-foreground">Send your first message!</p>
          </div>
        ) : (
          messages.map((message, index) => {
            const isOwn = message.sender_id === user?.id;
            const showAvatar = !isOwn && (index === 0 || messages[index - 1]?.sender_id !== message.sender_id);
            const isVoice = message.message_type === 'voice';
            const isImage = message.message_type === 'image';
            const isVideo = message.message_type === 'video';
            const isMedia = isImage || isVideo;
            
            return (
              <div
                key={message.id}
                className={cn("flex gap-2 group", isOwn && "justify-end")}
                data-testid={`message-${message.id}`}
              >
                {!isOwn && (
                  <div className="w-8">
                    {showAvatar && (
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={message.sender_avatar} />
                        <AvatarFallback className="text-xs bg-primary/10 text-primary">
                          {getInitials(message.sender_username)}
                        </AvatarFallback>
                      </Avatar>
                    )}
                  </div>
                )}
                
                {/* Delete button for own messages - shows on left when own message */}
                {isOwn && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="w-6 h-6 transition-opacity self-center hover:bg-secondary/50"
                        data-testid={`delete-message-${message.id}`}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem 
                        onClick={() => handleDeleteMessage(message.id)}
                        className="text-destructive"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Message
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                
                <div className={cn("max-w-[75%]", isOwn && "order-first")}>
                  <div className={cn(
                    "rounded-2xl overflow-hidden",
                    !isMedia && "px-4 py-2",
                    isOwn 
                      ? "bg-primary text-primary-foreground rounded-br-sm" 
                      : "bg-secondary text-secondary-foreground rounded-bl-sm"
                  )}>
                    {isVoice ? (
                      <VoiceMessagePlayer 
                        audioData={message.content} 
                        duration={message.metadata?.duration} 
                      />
                    ) : isImage ? (
                      <img 
                        src={message.content} 
                        alt="Foto" 
                        className="max-w-full max-h-64 object-contain cursor-pointer"
                        onClick={() => window.open(message.content, '_blank')}
                      />
                    ) : isVideo ? (
                      <video 
                        src={message.content} 
                        controls 
                        className="max-w-full max-h-64"
                      />
                    ) : (
                      <p className="break-words">{message.content}</p>
                    )}
                  </div>
                  <p className={cn(
                    "text-xs text-muted-foreground mt-1",
                    isOwn && "text-right"
                  )}>
                    {formatTimeAgo(message.created_at, language)}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="sticky bottom-0 bg-background border-t border-border/50 safe-bottom">
        {/* Media Preview */}
        {mediaPreview && (
          <div className="p-4 border-b border-border/50 relative">
            <div className="relative w-32 h-32 rounded-lg overflow-hidden">
              {mediaPreview.type === 'image' ? (
                <img 
                  src={mediaPreview.url} 
                  alt="Preview" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <video 
                  src={mediaPreview.url} 
                  className="w-full h-full object-cover"
                />
              )}
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="absolute top-1 right-1 w-6 h-6 rounded-full"
                onClick={cancelMediaPreview}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <Button
              type="button"
              className="mt-2 rounded-full"
              onClick={handleSendMedia}
              disabled={sending}
            >
              {sending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send {mediaPreview.type === 'image' ? 'Photo' : 'Video'}
            </Button>
          </div>
        )}

        <form onSubmit={handleSend} className="p-4">
          <div className="flex items-center gap-2">
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="media-file-input"
            />
            
            {/* Media button */}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="w-10 h-10 rounded-full shrink-0"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending}
              data-testid="media-upload-btn"
            >
              <Image className="w-5 h-5" />
            </Button>

            <EmojiPickerButton 
              onEmojiSelect={(emoji) => setNewMessage(prev => prev + emoji)} 
            />
            <VoiceRecorder onSend={handleSendVoice} disabled={sending} />
            <Input
              ref={inputRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={tr('typeMessage')}
              className="h-12 rounded-full bg-secondary/50 flex-1"
              data-testid="message-input"
            />
            <Button 
              type="submit" 
              size="icon" 
              className="w-12 h-12 rounded-full shrink-0"
              disabled={!newMessage.trim() || sending}
              data-testid="send-message-btn"
            >
              {sending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
